import { useState } from 'react'
import { segment } from '../lib/highlight'
import type { Measurement, Run } from '../lib/api'

// Shared by the wizard's own final steps (Onboarding.tsx, right after starting a run) and
// the standalone /app/runs/:id page (RunView.tsx) — factored out so both render identical
// running/results UI against identical polling logic, rather than two components drifting
// apart. This is also what makes the wizard's own copy ("je mag dit venster sluiten — we
// sturen een melding zodra het klaar is") actually true: closing the wizard's tab loses
// nothing, because /app/runs/:id polls the same GET /api/runs/:id and renders the same
// thing (app-shell-and-navigation plan §1c).

export function RunningStatus({ run }: { run: Run | null }) {
  const progress = run?.turns_completed ?? 0
  const total = run?.turns_planned ?? 0
  return (
    <div>
      <p className="mb-4 text-ink-soft">
        We sturen elke prompt naar elk gekozen AI-model. Dit duurt meestal 1-2 minuten.
      </p>
      <div className="mb-2 h-2 overflow-hidden rounded-full border border-line bg-card">
        <div
          className="h-full bg-amber transition-all"
          style={{ width: total > 0 ? `${Math.min(100, (progress / total) * 100)}%` : '10%' }}
        />
      </div>
      <p className="text-sm text-ink-soft">
        {total > 0 ? `${progress} van ${total} metingen` : 'Bezig met starten…'}
      </p>
    </div>
  )
}

export function ResultsStatus({
  run,
  doneLabel,
  onDone,
  onRetryFailed,
  busy,
}: {
  run: Run | null
  doneLabel: string
  onDone: () => void
  onRetryFailed: (languages?: string[]) => void
  busy?: boolean
}) {
  if (!run) return <p className="text-ink-soft">Resultaten laden…</p>

  const measurements = run.measurements ?? []
  const mentionedCount = measurements.filter((m) => m.mentioned).length
  const failedTurns = run.turns_planned - (run.turns_completed ?? 0)
  const partiallyFailed = run.status === 'failed' && (run.turns_completed ?? 0) > 0
  const fullyFailed = run.status === 'failed' && (run.turns_completed ?? 0) === 0

  if (fullyFailed) {
    return (
      <div>
        <div className="mb-4 rounded-lg border border-[#C1666B] bg-[#C1666B22] px-4 py-2.5 text-sm text-ink">
          <p>De meting is mislukt. Je credits zijn volledig terugbetaald.</p>
          {run.error && <p className="mt-1.5 text-ink-soft">Reden: {run.error}</p>}
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => onRetryFailed()}
          className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
        >
          Opnieuw proberen
        </button>
      </div>
    )
  }

  return (
    <div>
      {partiallyFailed && (
        <div className="mb-4 rounded-lg border border-amber bg-[#E8A33D22] px-4 py-2.5 text-sm text-ink">
          <p>
            Niet alles is gelukt: {run.turns_completed} van de {run.turns_planned} metingen. Je credits voor de
            mislukte {failedTurns} zijn terugbetaald.{' '}
            <button
              type="button"
              disabled={busy}
              onClick={() => onRetryFailed(run.failed_languages)}
              className="text-amber underline disabled:opacity-50"
            >
              Opnieuw proberen
            </button>
          </p>
          {run.error && <p className="mt-1.5 text-ink-soft">Reden: {run.error}</p>}
        </div>
      )}
      <div className="mb-6 rounded-xl border border-line bg-card p-5">
        <p className="text-lg font-semibold text-ink">
          {mentionedCount} van de {measurements.length} metingen noemen je merk
        </p>
      </div>
      <p className="mb-2 text-xs text-ink-soft">
        Genoemd/niet genoemd wordt per antwoord door AI bepaald, direct na het ophalen van het antwoord. Klik op een
        antwoord om het volledig te lezen.
      </p>
      <ul className="mb-6 flex flex-col gap-2">
        {measurements.map((m, i) => (
          <MeasurementRow key={i} measurement={m} brandName={run.brand_name} />
        ))}
      </ul>
      <button type="button" onClick={onDone} className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90">
        {doneLabel}
      </button>
    </div>
  )
}

// MeasurementRow shows the full AI response on demand, independent of the mentioned
// badge — excerpt alone is only ever set when mentioned is true (it's the AI's OWN
// supporting quote from its mention-check step), so a "Niet genoemd" turn previously had
// nothing to read at all, no way to judge that verdict for yourself.
// Highlighted returns the answer with the brand and the model's own supporting quote
// marked up, so "Genoemd" is auditable rather than asserted.
function Highlighted({ text, brand, excerpt }: { text: string; brand?: string; excerpt?: string }) {
  const parts = segment(text, brand ?? '', excerpt)
  return (
    <>
      {parts.map((p, i) =>
        p.kind === 'plain' ? (
          <span key={i}>{p.text}</span>
        ) : (
          <mark
            key={i}
            className={
              p.kind === 'excerpt'
                ? 'rounded bg-amber/70 px-0.5 text-[#0E2A3B]'
                : 'rounded bg-amber/25 px-0.5 text-ink'
            }
          >
            {p.text}
          </mark>
        ),
      )}
    </>
  )
}

function MeasurementRow({ measurement: m, brandName }: { measurement: Measurement; brandName?: string }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <li className="rounded-lg border border-line bg-card px-4 py-3 text-sm">
      <div className="mb-1 flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* The question comes first and the model second. A list of rows that all say
              "chatgpt / Niet genoemd" is unreadable: which question was asked is the thing
              a reader is actually looking for. */}
          {m.prompt_text && <p className="mb-0.5 text-ink">{m.prompt_text}</p>}
          <span className="text-xs text-ink-soft">
            {m.model}
            {m.language && ` · ${m.language}`}
          </span>
        </div>
        <span className={`shrink-0 ${m.mentioned ? 'text-[#5cc7b8]' : 'text-ink-soft'}`}>
          {m.mentioned ? 'Genoemd' : 'Niet genoemd'}
        </span>
      </div>
      {m.excerpt && <p className="text-ink-soft">{m.excerpt}</p>}
      {/* What the answer pointed the reader at. This is the actionable half: if an
          assistant names a competitor and cites a magazine, being covered there is the
          lever. Only what the text itself named, never the model's guess at its own
          training data. */}
      {m.sources && m.sources.length > 0 && (
        <p className="mt-1 text-xs text-ink-soft">
          <span className="text-ink">Genoemde bronnen:</span> {m.sources.join(', ')}
        </p>
      )}
      {m.raw_answer && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1.5 text-xs text-amber hover:underline"
          >
            {expanded ? 'Verberg volledig antwoord' : 'Toon volledig antwoord'}
          </button>
          {expanded && (
            <p className="mt-1.5 whitespace-pre-wrap border-t border-line pt-1.5 text-ink-soft">
              <Highlighted text={m.raw_answer} brand={brandName} excerpt={m.excerpt} />
            </p>
          )}
        </>
      )}
    </li>
  )
}
